import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
    ArrowRight,
    Award,
    Bike,
    Building2,
    GitCompareArrows,
    MapPin,
    MessageCircle,
    ShieldCheck,
    Sparkles,
    Users,
} from "lucide-react";
import PageSeo from "@/components/site/PageSeo";
import { useSettings, usePageCopy, useSiteStats } from "@/contexts/SettingsContext";
import { useSiteContact } from "@/lib/siteContact";

const SECTION_ICONS = [Building2, Bike, ShieldCheck, MapPin];

const fadeUp = {
    initial: { opacity: 0, y: 24 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-40px" },
    transition: { duration: 0.5 },
};

const AboutUsPage = () => {
    const { pages = {} } = useSettings() || {};
    const stats = useSiteStats();
    const contact = useSiteContact();
    const copy = usePageCopy("about", {
        title: "About Servall Bikes",
        subtitle: "Multi-brand authorized two-wheeler retail — compare, quote, and buy from showrooms you can trust.",
    });

    const sections = [
        { title: pages.about_sec1_title, body: pages.about_sec1_body },
        { title: pages.about_sec2_title, body: pages.about_sec2_body },
        { title: pages.about_sec3_title, body: pages.about_sec3_body },
        { title: pages.about_sec4_title, body: pages.about_sec4_body },
    ].filter((s) => s.title && s.body);

    const statCards = [
        { label: "Outlets", value: stats.total_dealers || "38+", icon: MapPin },
        { label: "Brands", value: stats.total_brands || "12+", icon: Award },
        { label: "Bikes listed", value: stats.total_bikes || "40+", icon: Bike },
        {
            label: "Riders served",
            value: pages.trust_use_live_stats !== false
                ? `${pages.trust_stat_riders_value || 1}+ Lakh`
                : "1+ Lakh",
            icon: Users,
        },
    ];

    const pillars = [
        { icon: ShieldCheck, title: "Authorized retail", desc: "Official multi-brand showrooms — not brokers or grey market." },
        { icon: GitCompareArrows, title: "Compare before you buy", desc: "Specs, EMI, and shortlists online — then visit for a test ride." },
        { icon: Sparkles, title: "Clear on-road pricing", desc: "Ex-showroom on the site. Full quote with RTO & insurance at the outlet." },
    ];

    return (
        <div className="bg-white text-[#0A2540] overflow-x-hidden" data-testid="about-page">
            <PageSeo pageKey="about" />

            {/* Hero */}
            <section className="relative bg-gradient-to-br from-[#0A2540] via-[#142b4d] to-[#0A2540] text-white py-20 md:py-28 overflow-hidden">
                <div className="absolute -top-32 -right-32 w-[28rem] h-[28rem] rounded-full bg-[#E31837]/25 blur-3xl pointer-events-none" />
                <div className="absolute -bottom-24 -left-24 w-80 h-80 rounded-full bg-[#FFD400]/10 blur-3xl pointer-events-none" />
                <div className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{
                    backgroundImage: "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)",
                    backgroundSize: "48px 48px",
                }} />

                <div className="container-x relative z-10">
                    <motion.div {...fadeUp} className="max-w-3xl">
                        <span className="inline-flex items-center gap-2 text-[11px] font-bold text-[#FFD400] tracking-[0.2em] uppercase mb-4">
                            <Sparkles size={14} /> {copy.eyebrow || "Who we are"}
                        </span>
                        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.08] tracking-tight">
                            {copy.title}
                        </h1>
                        <p className="text-zinc-300/95 mt-5 text-base sm:text-lg leading-relaxed max-w-2xl">
                            {copy.subtitle}
                        </p>
                        <div className="flex flex-wrap gap-3 mt-8">
                            <Link
                                to="/bikes"
                                className="inline-flex items-center gap-2 bg-[#E31837] text-white font-bold text-xs uppercase tracking-wide px-6 py-3.5 rounded-xl shadow-lg shadow-[#E31837]/30 hover:bg-white hover:text-[#0A2540] transition-all duration-300"
                            >
                                Browse bikes <ArrowRight size={14} />
                            </Link>
                            <Link
                                to="/dealers"
                                className="inline-flex items-center gap-2 border border-white/25 text-white font-bold text-xs uppercase tracking-wide px-6 py-3.5 rounded-xl hover:bg-white/10 transition-all"
                            >
                                <MapPin size={14} /> Our outlets
                            </Link>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Stats strip */}
            <section className="relative z-10 -mt-8 md:-mt-10 container-x">
                <motion.div
                    {...fadeUp}
                    className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4"
                >
                    {statCards.map(({ label, value, icon: Icon }) => (
                        <div
                            key={label}
                            className="bike-card p-5 md:p-6 bg-white border border-zinc-200/80 shadow-lg shadow-zinc-200/40 text-center"
                        >
                            <div className="w-11 h-11 rounded-xl bg-[#E31837]/10 text-[#E31837] flex items-center justify-center mx-auto mb-3">
                                <Icon size={20} />
                            </div>
                            <p className="font-display font-black text-2xl md:text-3xl text-[#0A2540]">{value}</p>
                            <p className="text-[11px] font-bold uppercase tracking-wider text-zinc-500 mt-1">{label}</p>
                        </div>
                    ))}
                </motion.div>
            </section>

            {/* Story sections — alternating layout */}
            <section className="container-x py-16 md:py-24">
                <div className="max-w-5xl mx-auto space-y-16 md:space-y-24">
                    {sections.length > 0 ? sections.map((sec, i) => {
                        const Icon = SECTION_ICONS[i] || Building2;
                        const even = i % 2 === 0;
                        return (
                            <motion.div
                                key={sec.title}
                                {...fadeUp}
                                transition={{ duration: 0.5, delay: i * 0.05 }}
                                className={`flex flex-col gap-8 md:gap-12 items-center ${even ? "md:flex-row" : "md:flex-row-reverse"}`}
                            >
                                <div className="flex-1 w-full">
                                    <div className="inline-flex items-center gap-2 mb-4">
                                        <span className="w-9 h-9 rounded-lg bg-[#E31837]/10 text-[#E31837] flex items-center justify-center">
                                            <Icon size={18} />
                                        </span>
                                        <span className="text-xs font-bold text-[#E31837] uppercase tracking-wider">
                                            0{i + 1}
                                        </span>
                                    </div>
                                    <h2 className="font-display font-bold text-2xl sm:text-3xl md:text-4xl text-[#0A2540] leading-tight">
                                        {sec.title}
                                    </h2>
                                    <p className="mt-4 text-[#4a4a52] leading-relaxed text-sm sm:text-base whitespace-pre-line">
                                        {sec.body}
                                    </p>
                                </div>
                                <div className="flex-1 w-full">
                                    <div className={`relative rounded-2xl overflow-hidden aspect-[4/3] max-w-md mx-auto ${even ? "md:ml-auto" : "md:mr-auto"}`}>
                                        <div className={`absolute inset-0 bg-gradient-to-br ${
                                            i === 0 ? "from-[#0A2540] to-[#E31837]/80" :
                                            i === 1 ? "from-[#142b4d] to-[#6366F1]/70" :
                                            i === 2 ? "from-[#E31837] to-[#0A2540]" :
                                            "from-[#10B981]/80 to-[#0A2540]"
                                        }`} />
                                        <div className="absolute inset-0 flex flex-col items-center justify-center p-8 text-white text-center">
                                            <Icon size={48} className="opacity-90 mb-4" strokeWidth={1.25} />
                                            <p className="font-display font-bold text-lg opacity-95">{sec.title}</p>
                                        </div>
                                        <div className="absolute -bottom-6 -right-6 w-32 h-32 rounded-full bg-white/10 blur-2xl" />
                                    </div>
                                </div>
                            </motion.div>
                        );
                    }) : (
                        <p className="text-center text-[#7a7a84]">About page content is being updated.</p>
                    )}
                </div>
            </section>

            {/* Pillars */}
            <section className="bg-[#F4F6F8] border-y border-zinc-100 py-16 md:py-20">
                <div className="container-x">
                    <motion.div {...fadeUp} className="text-center max-w-2xl mx-auto mb-10 md:mb-14">
                        <p className="text-[11px] font-semibold text-[#E31837] tracking-wide uppercase">Our promise</p>
                        <h2 className="font-display text-3xl sm:text-4xl text-[#0A2540] mt-2">Why riders choose Servall</h2>
                    </motion.div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6 max-w-5xl mx-auto">
                        {pillars.map((p, i) => {
                            const Icon = p.icon;
                            return (
                                <motion.div
                                    key={p.title}
                                    {...fadeUp}
                                    transition={{ delay: i * 0.08 }}
                                    className="bg-white rounded-2xl border border-zinc-200 p-6 md:p-8 hover:border-[#E31837]/30 hover:shadow-lg hover:shadow-[#E31837]/5 transition-all duration-300"
                                >
                                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#E31837] to-[#c01430] text-white flex items-center justify-center mb-4 shadow-md shadow-[#E31837]/25">
                                        <Icon size={22} />
                                    </div>
                                    <h3 className="font-display font-bold text-lg text-[#0A2540]">{p.title}</h3>
                                    <p className="text-sm text-zinc-500 mt-2 leading-relaxed">{p.desc}</p>
                                </motion.div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="relative bg-[#0A2540] text-white py-16 md:py-24 overflow-hidden">
                <div className="absolute -top-20 -right-20 w-96 h-96 rounded-full bg-[#E31837]/20 blur-3xl pointer-events-none" />
                <div className="absolute -bottom-20 -left-20 w-80 h-80 rounded-full bg-[#FFD400]/5 blur-3xl pointer-events-none" />
                <motion.div {...fadeUp} className="container-x relative z-10 max-w-3xl mx-auto text-center">
                    <Sparkles className="w-8 h-8 text-[#FFD400] mx-auto mb-4" />
                    <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl leading-tight">
                        Ready to find your perfect ride?
                    </h2>
                    <p className="text-zinc-300 mt-4 text-sm sm:text-base max-w-xl mx-auto">
                        Browse inventory, compare up to three bikes, or get today&apos;s best on-road quote from a Servall outlet near you.
                    </p>
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-8">
                        <Link
                            to="/bikes"
                            className="inline-flex items-center gap-2 bg-[#E31837] text-white font-bold text-xs uppercase tracking-wide px-8 py-4 rounded-xl shadow-lg shadow-[#E31837]/40 hover:bg-white hover:text-[#0A2540] transition-all min-h-[48px] w-full sm:w-auto justify-center"
                        >
                            Browse bikes <ArrowRight size={14} />
                        </Link>
                        <Link
                            to="/compare"
                            className="inline-flex items-center gap-2 border border-white/30 text-white font-bold text-xs uppercase tracking-wide px-6 py-4 rounded-xl hover:bg-white/10 transition-all min-h-[48px] w-full sm:w-auto justify-center"
                        >
                            <GitCompareArrows size={16} /> Compare models
                        </Link>
                        <a
                            href={contact.whatsappUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 bg-[#25D366] text-white font-bold text-xs uppercase tracking-wide px-6 py-4 rounded-xl hover:bg-[#1ea952] transition-all min-h-[48px] w-full sm:w-auto justify-center"
                            data-testid="about-whatsapp"
                        >
                            <MessageCircle size={16} /> WhatsApp
                        </a>
                    </div>
                </motion.div>
            </section>
        </div>
    );
};

export default AboutUsPage;
